/**
 * Hook for the editor's rAF render loop.
 * Subscribes to store.renderVersion and re-draws scene + chrome.
 */
import { useEffect, useRef, useCallback } from 'react';
import type { Surface } from 'canvaskit-wasm';
import { renderNode, parseColor, applyFillToPaint, getCachedImage, loadImage, type RenderContext } from './canvaskit-render-scene';
import { buildRenderDoc } from './canvaskit-render-doc';
import { LayoutAnimator, ANIMATE_LAYOUT_TRANSITIONS, computeFrozenIds } from './canvaskit-layout-animator';
import {
  drawSelectionBoxes,
  drawHandles,
  drawMultiSelectChrome,
  drawMeasurements,
  drawHoverOutline,
  drawSnapGuides,
  drawLayoutGuides,
  drawMarquee,
  drawLinkZones,
  drawNodeLinkOverlays,
  drawCropOverlay,
  drawVectorEditOverlay,
  drawPenToolPreview,
  drawFreehandPreview,
  drawReorderIndicator,
  drawNestTargetHighlight,
} from './canvaskit-render-chrome';
import { getNodeAABB, getSelectionAABB } from './canvaskit-hit-test';
import type { CanvasEditorStore } from './canvaskit-editor-store';
import type { SurfaceRefs } from './useCanvasKitSurface';

export function useEditorRenderer(
  store: CanvasEditorStore,
  getRefs: () => SurfaceRefs,
  ready: boolean,
  // Changes whenever the FontMgr is rebuilt (see useCanvasKitSurface.fontEpoch).
  // Forces a redraw so newly-loaded fonts repaint without waiting for a store
  // change.
  redrawSignal?: number,
) {
  const rafRef = useRef<number>(0);
  const lastVersionRef = useRef(-1);
  const settlingRef = useRef(false);
  const animatorRef = useRef<LayoutAnimator>(new LayoutAnimator());

  const doRender = useCallback(() => {
    rafRef.current = 0;
    const { ck, surface, fontMgr } = getRefs();
    if (!ck || !surface || !fontMgr) return;

    try {
    const state = store.getState();
    const { zoom, panOffset, selectedIds, hoveredId, snapGuides, marqueeRect, linkZonesVisible, guidesVisible, croppingNodeId, editingTextId, vectorEditingNodeId, vectorPaths, vectorSelectedPoints, penCurrentPath, freehandPoints, altMeasureActive } = state;
    // Apply auto-layout before rendering so child positions are always resolved.
    // When a reorder drag is in progress, also paint a preview: siblings shift
    // into their target arrangement, dragged node ghost-renders at cursor.
    const targetDoc = buildRenderDoc(state);
    // Ease node rects toward target so reorder/reflow glide instead of snapping
    // (any auto-layout section, not just the workspace). The manipulated subtree
    // is frozen so it tracks the cursor 1:1; free-form canvases with no
    // auto-layout never change a node's slot, so the animator stays inert there.
    let doc = targetDoc;
    if (ANIMATE_LAYOUT_TRANSITIONS) {
      const result = animatorRef.current.apply(targetDoc, performance.now(), computeFrozenIds(state));
      doc = result.doc;
      settlingRef.current = result.settling;
    }
    const dpr = window.devicePixelRatio || 1;

    const skCanvas = surface.getCanvas();
    skCanvas.clear(ck.Color(232, 232, 232, 1));
    skCanvas.save();
    skCanvas.scale(dpr, dpr);

    // The "page" background is glued to the single artboard root (the email /
    // social page) so it tracks the frame when it's moved on the infinite
    // canvas. Drawing it at the doc origin instead left a white ghost behind
    // whenever the artboard was dragged away. Loose/legacy and multi-root
    // (workspace) docs have no single page frame, so they fall back to the doc
    // origin + size as before.
    const pageRoot = doc.rootChildren.length === 1 ? doc.nodes[doc.rootChildren[0]] : null;
    const canvasX = panOffset.x + (pageRoot?.x ?? 0) * zoom;
    const canvasY = panOffset.y + (pageRoot?.y ?? 0) * zoom;
    const canvasW = (pageRoot?.width ?? doc.width) * zoom;
    const canvasH = (pageRoot?.height ?? doc.height) * zoom;

    // Canvas background
    const bgPaint = new ck.Paint();
    bgPaint.setStyle(ck.PaintStyle.Fill);
    bgPaint.setColor(ck.Color(255, 255, 255, 1));
    skCanvas.drawRect(ck.LTRBRect(canvasX, canvasY, canvasX + canvasW, canvasY + canvasH), bgPaint);
    if (doc.fills?.length) {
      skCanvas.save();
      skCanvas.translate(canvasX, canvasY);
      skCanvas.scale(zoom, zoom);
      const bgRect = ck.LTRBRect(0, 0, doc.width, doc.height);
      for (const fill of doc.fills) {
        if ((fill as any).visible === false) continue;
        if (fill.type === 'IMAGE' && (fill as any).src) {
          const cached = getCachedImage((fill as any).src, Math.max(doc.width, doc.height));
          if (cached) {
            const imgW = cached.width();
            const imgH = cached.height();
            const s = Math.max(doc.width / imgW, doc.height / imgH);
            const cropW = doc.width / s;
            const cropH = doc.height / s;
            const cropX = (imgW - cropW) / 2;
            const cropY = (imgH - cropH) / 2;
            const imgPaint = new ck.Paint();
            if (fill.opacity != null && fill.opacity < 1) imgPaint.setAlphaf(fill.opacity);
            skCanvas.drawImageRect(
              cached,
              ck.LTRBRect(cropX, cropY, cropX + cropW, cropY + cropH),
              bgRect,
              imgPaint,
            );
            imgPaint.delete();
          } else {
            loadImage(ck, (fill as any).src).then(() => store.getState().requestRender());
          }
        } else {
          const fp = new ck.Paint();
          fp.setStyle(ck.PaintStyle.Fill);
          applyFillToPaint(ck, fp, fill, doc.width, doc.height);
          skCanvas.drawRect(bgRect, fp);
          fp.delete();
        }
      }
      skCanvas.restore();
    }
    bgPaint.delete();

    // Scene nodes
    skCanvas.save();
    skCanvas.translate(panOffset.x, panOffset.y);
    skCanvas.scale(zoom, zoom);

    const ctx: RenderContext = {
      ck,
      canvas: skCanvas,
      doc,
      fontMgr,
      onImageLoaded: () => store.getState().requestRender(),
      editingTextId,
      zoom,
    };

    for (const rootId of doc.rootChildren) {
      const node = doc.nodes[rootId];
      if (node) renderNode(ctx, node);
    }

    skCanvas.restore();

    // Column layout guides: over the scene, under all chrome. The master
    // toggle (Shift+G) plus each config's own visibility are honoured inside.
    drawLayoutGuides(ck, skCanvas, doc, guidesVisible, zoom, panOffset.x, panOffset.y);

    // Link overlays (only visible when Links tab is active): manual zone grids
    // plus every node that carries its own linkUrl (agent-authored links).
    if (linkZonesVisible) {
      drawLinkZones(ck, skCanvas, doc, zoom, panOffset.x, panOffset.y);
      drawNodeLinkOverlays(ck, skCanvas, doc, zoom, panOffset.x, panOffset.y);
    }

    // Editor chrome
    if (croppingNodeId) {
      // In crop mode: draw crop overlay instead of normal selection chrome
      drawCropOverlay(ck, skCanvas, doc, croppingNodeId, zoom, panOffset.x, panOffset.y);
    } else if (vectorEditingNodeId && vectorPaths) {
      // In vector edit mode: draw point overlay instead of normal handles
      drawSelectionBoxes(ck, skCanvas, doc, selectedIds, zoom, panOffset.x, panOffset.y);
      drawVectorEditOverlay(ck, skCanvas, doc, vectorEditingNodeId, vectorPaths, vectorSelectedPoints, zoom, panOffset.x, panOffset.y);
    } else {
      if (hoveredId && !selectedIds.has(hoveredId)) {
        drawHoverOutline(ck, skCanvas, doc, hoveredId, selectedIds, zoom, panOffset.x, panOffset.y);
      }
      drawSelectionBoxes(ck, skCanvas, doc, selectedIds, zoom, panOffset.x, panOffset.y);
      drawHandles(ck, skCanvas, doc, selectedIds, zoom, panOffset.x, panOffset.y);
      // 2+ nodes: combined bounding box + resize handles on top of the
      // per-node selection outlines.
      drawMultiSelectChrome(ck, skCanvas, doc, selectedIds, zoom, panOffset.x, panOffset.y);
      if (state.dragReorder) {
        drawReorderIndicator(ck, skCanvas, targetDoc, state.dragReorder, zoom, panOffset.x, panOffset.y);
      }
      if (state.dragDropTarget) {
        drawNestTargetHighlight(ck, skCanvas, targetDoc, state.dragDropTarget, zoom, panOffset.x, panOffset.y);
      }
      // Alt-hover measurements: distances from the selection box to the hovered
      // (non-selected) node, or — when nothing else is hovered — to the page
      // artboard edges. Only a hover affordance, so never while dragging.
      if (altMeasureActive && selectedIds.size > 0 && state.interactionMode === 'idle') {
        const selAABB = getSelectionAABB(doc, selectedIds);
        if (selAABB) {
          let targetId: string | null = null;
          if (hoveredId && !selectedIds.has(hoveredId) && doc.nodes[hoveredId]) {
            targetId = hoveredId;
          } else {
            const rootId = doc.rootChildren.length === 1 ? doc.rootChildren[0] : null;
            if (rootId && !selectedIds.has(rootId)) targetId = rootId;
          }
          if (targetId) {
            drawMeasurements(
              ck,
              skCanvas,
              fontMgr,
              selAABB,
              getNodeAABB(doc, targetId),
              zoom,
              panOffset.x,
              panOffset.y,
            );
          }
        }
      }
    }

    if (snapGuides.length > 0) {
      drawSnapGuides(ck, skCanvas, snapGuides, doc.width, doc.height, zoom, panOffset.x, panOffset.y);
    }
    if (marqueeRect) {
      drawMarquee(ck, skCanvas, marqueeRect, zoom, panOffset.x, panOffset.y);
    }
    if (penCurrentPath && penCurrentPath.points.length > 0) {
      drawPenToolPreview(ck, skCanvas, penCurrentPath, zoom, panOffset.x, panOffset.y);
    }
    if (freehandPoints && freehandPoints.length > 1) {
      drawFreehandPreview(ck, skCanvas, freehandPoints, zoom, panOffset.x, panOffset.y);
    }

    skCanvas.restore();
    surface.flush();

    // Keep the loop alive while motion is in flight so it eases to rest.
    if (settlingRef.current && !rafRef.current) {
      rafRef.current = requestAnimationFrame(doRender);
    }
    } catch (err) {
      console.error('CanvasKit render error:', err);
    }
  }, [store, getRefs]);

  // Subscribe to renderVersion
  useEffect(() => {
    const unsub = store.subscribe((state) => {
      if (state.renderVersion !== lastVersionRef.current) {
        lastVersionRef.current = state.renderVersion;
        // Guard against double-scheduling: a settle-loop frame may already be
        // pending. doRender clears rafRef before drawing so changes re-arm.
        if (!rafRef.current) rafRef.current = requestAnimationFrame(doRender);
      }
    });
    return () => {
      unsub();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [store, doRender]);

  // Initial render (and re-render when the FontMgr is rebuilt).
  useEffect(() => {
    if (ready) {
      requestAnimationFrame(doRender);
    }
  }, [ready, doRender, redrawSignal]);

  return { doRender };
}
